import React from "react";

import { ColocaAmountInputControlled } from "@/shared/components/forms/ColocaAmountInput";
import { ColocaCombobox, type ColocaComboboxOption } from "@/shared/components/forms/ColocaCombobox";
import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { cn } from "@/shared/lib/utils";

// Form primitives for the dispersion wizard: thin label/error composition layers over the DS controls, so the
// wizard's fields are the same controls the rest of the app uses. Every one of these delegates — none of them
// styles an `<input>` of its own, which is what made this screen read as pre-design-system.

const LABEL = "text-sm font-semibold text-foreground";

export const FieldLabel: React.FC<{
  htmlFor?: string;
  optional?: boolean;
  children: React.ReactNode;
}> = ({ htmlFor, optional, children }) => (
  <label htmlFor={htmlFor} className={LABEL}>
    {children}
    {optional && <span className="ml-1 font-normal text-muted-foreground">(opcional)</span>}
  </label>
);

export interface FieldSelectOption {
  value: string;
  label: string;
  /** Supports the "coming soon" state for destinations not yet enabled. */
  isDisabled?: boolean;
  /**
   * Right-aligned amber tag rendered next to the label (menu and selected value) — e.g. the "Not verified"
   * KYC mark on client options. Search keeps matching on `label` only.
   */
  badge?: string;
}

interface FieldSelectProps {
  id?: string;
  label?: string;
  optional?: boolean;
  /** Right-aligned action rendered on the label row (e.g. the "register client" shortcut). */
  labelAction?: React.ReactNode;
  value: string;
  options: FieldSelectOption[];
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  containerClassName?: string;
}

/** Right-aligned amber tag next to the option label (e.g. the "Not verified" KYC mark). Display-only. */
const OptionBadgeRow: React.FC<{ label: string; badge: string }> = ({ label, badge }) => (
  <span className="flex w-full items-center justify-between gap-2">
    <span className="truncate">{label}</span>
    <span className="shrink-0 rounded-full border border-warning-container-foreground/20 bg-warning-container px-1.5 py-px text-[11px] font-semibold text-warning-container-foreground">
      {badge}
    </span>
  </span>
);

export const FieldSelect: React.FC<FieldSelectProps> = ({
  id,
  label,
  optional,
  labelAction,
  value,
  options,
  onValueChange,
  placeholder = "Selecciona…",
  disabled = false,
  searchable = true,
  containerClassName = "",
}) => {
  const comboboxOptions = React.useMemo<ColocaComboboxOption[]>(
    () => options.map((o) => ({ value: o.value, label: o.label, disabled: o.isDisabled })),
    [options],
  );
  const badgeByValue = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const o of options) if (o.badge) map.set(o.value, o.badge);
    return map;
  }, [options]);
  const renderOption = React.useCallback(
    (o: ColocaComboboxOption) => {
      const badge = badgeByValue.get(o.value);
      return badge ? <OptionBadgeRow label={o.label} badge={badge} /> : o.label;
    },
    [badgeByValue],
  );
  return (
    <div className={`flex min-w-0 flex-col gap-2 ${containerClassName}`}>
      {label &&
        (labelAction ? (
          <div className="flex items-center justify-between gap-2">
            <FieldLabel htmlFor={id} optional={optional}>
              {label}
            </FieldLabel>
            {labelAction}
          </div>
        ) : (
          <FieldLabel htmlFor={id} optional={optional}>
            {label}
          </FieldLabel>
        ))}
      {searchable ? (
        <ColocaCombobox
          id={id}
          value={value || null}
          onValueChange={(v) => onValueChange(v ?? "")}
          options={comboboxOptions}
          placeholder={placeholder}
          disabled={disabled}
          renderOption={renderOption}
          className="w-full"
        />
      ) : (
        <ColocaSelect
          id={id}
          value={value}
          onValueChange={onValueChange}
          options={options.map((o) => ({
            value: o.value,
            label: o.badge ? <OptionBadgeRow label={o.label} badge={o.badge} /> : o.label,
            disabled: o.isDisabled,
          }))}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full"
        />
      )}
    </div>
  );
};

interface FieldInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> {
  label?: string;
  optional?: boolean;
  error?: string;
  helper?: string;
  containerClassName?: string;
  value: string;
  onValueChange: (value: string) => void;
}

export const FieldInput: React.FC<FieldInputProps> = ({
  label,
  optional,
  error,
  helper,
  id,
  className = "",
  containerClassName = "",
  value,
  onValueChange,
  ...rest
}) => (
  <div className={cn("flex min-w-0 flex-col gap-2", containerClassName)}>
    {label && (
      <FieldLabel htmlFor={id} optional={optional}>
        {label}
      </FieldLabel>
    )}
    <ColocaInput
      id={id}
      value={value}
      onValueChange={onValueChange}
      aria-invalid={!!error}
      className={cn(error && "border-destructive focus-visible:ring-destructive/20", className)}
      {...rest}
    />
    {error ? (
      <span className="text-sm font-medium text-destructive" role="alert">
        {error}
      </span>
    ) : helper ? (
      <span className="text-sm text-muted-foreground">{helper}</span>
    ) : null}
  </div>
);

interface HeroAmountInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange"
> {
  label: string;
  /** Currency code rendered inside the field, right-aligned. */
  suffix: string;
  /** Shows a spinner next to the label while the other amount drives the quote. */
  loading?: boolean;
  error?: boolean;
  containerClassName?: string;
  value: string;
  onValueChange: (value: string) => void;
}

/**
 * The dual "you send / beneficiary receives" amount field. Delegates to `ColocaAmountInputControlled`, the
 * same control the swaps form uses, so both money screens have identical fields — it also sanitises to a
 * decimal string instead of letting arbitrary text reach the quote request.
 */
export const HeroAmountInput: React.FC<HeroAmountInputProps> = ({
  label,
  suffix,
  loading,
  error,
  id,
  containerClassName = "",
  value,
  onValueChange,
  ...rest
}) => (
  <div className={cn("flex min-w-0 flex-col gap-2", containerClassName)}>
    <div className="flex items-center gap-2">
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      {loading && (
        <span
          aria-hidden="true"
          className="size-3.5 shrink-0 animate-spin rounded-full border-2 border-border border-t-primary"
        />
      )}
    </div>
    {/* Grouped while typing: this is the figure somebody reads back to confirm they are sending fifteen
        hundred and not a hundred and fifty thousand. Display only — `onValueChange` still receives the plain
        decimal string the quote and the payout are built from. */}
    <ColocaAmountInputControlled
      id={id}
      value={value}
      onValueChange={onValueChange}
      suffix={suffix}
      groupThousands
      aria-invalid={error}
      inputClassName={cn(error && "border-destructive focus-visible:ring-destructive/20")}
      {...rest}
    />
  </div>
);
